/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { untranslated, type TranslatedString } from "@i18n";
import { startTransition, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import {
  formCategoryObject,
  getPresetSectionsObject,
} from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { optionsViewType } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/constant.helper";
import { formCategories, MAX_FORM_ANSWER_FILE_SIZE } from "@Helpers";
import {
  addValidationToForms,
  checkMandatoryFields,
  formSectionType,
  getSectionPermission,
  getUserPermissionForForm,
} from "./constants";
import _ from "lodash";
import { PLATFORM, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { SignTypeValues } from "@CubeComponents/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import useFormValidator from "../FormValidation/useFormValidator";
import { getContractDetails } from "@Features/contracts/state/contracts.actions";
import { getBackendUrl } from "@Utils";
import { folderPermissions, presetSectionsCodeObject } from "@Helpers/constants";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import { RootState } from "@Reducers/index";
import type { RecentAccessResponse } from "@ServerTypes/recentAccess/recentAccess.schema";
import type { LocationControllerResponse, TeamsControllerResponse } from "@ServerTypes/projects/project.schema";
import type {
  QuestionField,
  TaskInspectionAndProgressFormsControllerResponse,
  TipfUpdateRequest,
} from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormInstance, FormInstanceSection } from "../../formInstance.wire";
import type {
  FieldFileValue,
  FieldModalStateArgs,
  FieldUserValue,
  FileUploadChangeArgs,
  OpenAddUserModalArgs,
  RemoveAllUploadedFilesArgs,
  RemoveUploadedFileArgs,
  RemoveUserFromListArgs,
  SaveEditedImageArgs,
} from "./questionFieldsHelper";
import type { FormQuestionFieldsProps } from "./questionFields";
import { axiosWrapper } from "@AxiosWrapper";
import { archiveForms, getForm, updateForm } from "@Actions/Forms";
import { updateTaskInspectionAndProgressWithFile, type MultipartFile } from "@Actions/Forms";

type CustomFieldDataPayload = {
  eachField: QuestionField | undefined;
  answerValue: unknown;
  fieldIndex: number | null | undefined;
  sectionIndex: number | null | undefined;
  tableRowIndex?: number;
  source?: string;
  args?: unknown;
};
type HandleCustomFieldData = (payload: CustomFieldDataPayload) => void;
type HandleInputChangeArgs = Parameters<NonNullable<FormQuestionFieldsProps["handleInputChange"]>>[0] & {
  args?: unknown;
};
type WithFormInstanceResult<T> = T extends unknown ? Omit<T, "result"> & { result?: FormInstance[] } : never;

type FormsControllerResponseData = WithFormInstanceResult<TaskInspectionAndProgressFormsControllerResponse> & {
  message?: string;
  addedAttachmentData?: { obj?: Record<string, unknown> };
  autoFieldAnswers?: Record<string, unknown>;
};

export type FormsController = (
  payload: Record<string, unknown>,
  abortController?: AbortController,
) => Promise<{ data: FormsControllerResponseData } | void>;

type FormsControllerResult = { data: FormsControllerResponseData };
export type EachAnswerToSave = TipfUpdateRequest["data"] & {
  sectionIndex?: number;
  fieldIndex?: number;
  uploadFile?: PendingUpload;
};

export type PendingUpload = { file: MultipartFile; name: string };

type TableRow = Exclude<NonNullable<FormInstanceSection["tableDataSource"]>[number], undefined>;

const isTableRow = (row: unknown): row is TableRow => typeof row === "object" && row !== null && !Array.isArray(row);
const readTableRow = (row: unknown): TableRow => (isTableRow(row) ? row : {});

const readFileList = (value: unknown): FieldFileValue[] | undefined => (Array.isArray(value) ? value : undefined);

const readUserList = (value: unknown): FieldUserValue[] | undefined => (Array.isArray(value) ? value : undefined);

type UploadCandidate = { type?: string; name?: string; fileName?: string; size?: number; uri?: string };

type FileInputEvent = { target: { files?: FileList | null; value: string } };

type FormActionDetails = { comment: string; signDoc: string | null; signType: string };

type CommentDialogState = { status: boolean; source: string; heading: string; btnText: string };

type AddUsersModalState = {
  status: boolean;
  fieldData: OpenAddUserModalArgs["eachField"];
  fieldIndex: OpenAddUserModalArgs["fieldIndex"] | undefined;
  sectionIndex: OpenAddUserModalArgs["sectionIndex"] | undefined;
  section: OpenAddUserModalArgs["section"];
  source: string;
  tableRowIndex: number | undefined;
  uniqueId: string;
  showCrossIcon?: boolean;
};

type WarningModalState = { status: boolean; source?: string; data?: FormInstanceSection };

export type TableSectionConfig = {
  height: string;
  expandMode: boolean;
  section: FormInstanceSection;
  sectionIndex: number;
};

type ExportModalState = { state: boolean; paperSize: string; tableFormat: string };

type UrlModalState = FieldModalStateArgs & { answerValue?: unknown; uniqueId?: string };

type GeoLocationModalState = FieldModalStateArgs & { uniqueId?: string };

interface UseFormsProps {
  navigate?: (to: string) => void;
  params?: Readonly<Record<string, string | undefined>>;
  scheduleId?: ObjectIdString;
  contractId?: ObjectIdString;
  resourceId?: ObjectIdString;

  userDetails?: RootState["userDetails"];
  portalData?: RootState["portalData"];
  projectData?: RootState["projectData"];
  getCurrencyData?: typeof import("@Actions/Currency").getCurrencyData;
  getContractDetailUrl?: (props: {
    selectedTab?: string;
    projectId?: string;
    contractId?: string;
    versionId?: string;
  }) => string;
  getFormsListUrl?: (props: { selectedTab?: string; projectId?: string; resourceType?: string }) => string;
  uuid?: () => string;
  downloadFile?: typeof import("@Services/fileDownload").downloadFile;
  mobile?: boolean;
  navigation?: { replace: (screen: string, params: Record<string, unknown>) => void };
  bottomSheetRefFiles?: { current?: { present: () => void } | null };
  bottomSheetRefUsers?: { current?: { present: () => void } | null };
  bottomSheetRefAddedUsers?: { current?: { present: () => void } | null };
  projectId?: string;
}
const useForms = ({
  navigate,
  params,
  contractId,
  resourceId,
  userDetails,
  portalData,
  projectData,
  getCurrencyData,
  getContractDetailUrl,
  getFormsListUrl,
  uuid,
  mobile = false,
  navigation,
  bottomSheetRefFiles,
  bottomSheetRefUsers,
  bottomSheetRefAddedUsers,
  projectId,
}: UseFormsProps) => {
  const { t } = useTranslation();
  const presetSectionsObject = getPresetSectionsObject();
  const dispatch = useDispatch();
  const abortControllerList = useRef<Record<string, AbortController[]>>({});
  const { RESOURCE_MODIFY } = folderPermissions();
  const customModules = useSelector((state: RootState) => state.customModules);
  const [levelEditModal, setLevelEditModal] = useState<{
    open: boolean;
    levelIndex: number | null;
    editedLevel?: TipfUpdateRequest["data"]["editedLevel"];
    isValid?: boolean;
  }>({ open: false, levelIndex: null });
  const selectSignType = (args: string) => {
    const signType = args;
    if (!userDetails!.signature && signType == SignTypeValues.IMPORT_PROFILE_SIGNATURE) {
      dispatch(
        showToastNotification({
          content: t("Profile sign not set") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      setFormActionDetails((prev: FormActionDetails) => ({
        ...prev,
        signType,
        signDoc: null,
      }));
    }
  };

  const [inspectionData, setInspectionData] = useState<FormInstance>({} as FormInstance);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentModalOpenedFor, setAttachmentModalOpenedFor] = useState<{
    sectionIndex?: number;
    fieldIndex?: number;
  }>({});
  const [sideBarTaskDetails, setSideBarTaskDetails] = useState<unknown[]>([]);
  const [eachAnswerToSave, setEachAnswerToSave] = useState<EachAnswerToSave>({});
  const [answerSaved, setAnswerSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<{ timeStamp: number; userId: ObjectIdString } | null | undefined>(null);
  const [isFileUploading, setIsFileUploading] = useState<Array<{ type?: string; uniqueId: string }>>([]);
  const inFlightUploadsRef = useRef<Record<string, { count: number; value: unknown[] }>>({});
  const [openCommentDialog, setOpenCommentDialog] = useState<CommentDialogState>({
    status: false,
    source: "",
    heading: "",
    btnText: "",
  });
  const [formActionDetails, setFormActionDetails] = useState<FormActionDetails>({
    comment: "",
    signDoc: null,
    signType: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
  });
  const [accordionExpandedState, setAccordionExpandedState] = useState<string[]>([]);
  const [showSideBar, setShowSideBar] = useState(true);
  const [showAddUsersModal, setShowAddUsersModal] = useState<AddUsersModalState>({
    status: false,
  } as AddUsersModalState);
  const [showWarningModal, setShowWarningModal] = useState<WarningModalState>({ status: false });
  const [modifySection, setModifySection] = useState<string[]>([]);
  const [actionBtnDisabled, setActionBtnDisabled] = useState(false);
  const [tableSectionConfig, setTableSectionConfig] = useState<TableSectionConfig>({
    height: "",
    expandMode: false,
  } as TableSectionConfig);
  const [showWaitSpinner, setShowWaitSpinner] = useState(false);
  const formValidator = useFormValidator({});

  const [teamData, setTeamData] = useState<unknown>(null);
  const [loactionData, setLoactionData] = useState<unknown>(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [imageFieldSelectedIndex, setImageFieldSelectedIndex] = useState<number | null>(null);
  const [openExportModal, setOpenExportModal] = useState<ExportModalState>({
    state: false,
    paperSize: "A4",
    tableFormat: "card",
  });
  const [showUrlModal, setShowUrlModal] = useState<UrlModalState>({ state: false } as UrlModalState);
  const [showGeoLocationModal, setShowGeoLocationModal] = useState<GeoLocationModalState>({
    state: false,
  } as GeoLocationModalState);

  const formObject = useRef<unknown>(null);

  const commentSaveDisabledState =
    openCommentDialog?.source !== "rollBack" &&
    (formActionDetails.comment.trim() == "" ||
      (formActionDetails.signType === SignTypeValues.ATTACH_SIGNED_DOCUMENT && formActionDetails.signDoc == null));

  const ActionItems = inspectionData._id
    ? [
        ...(inspectionData.isSubmitted
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.approveBtnTxt}`,
                id: "approve",
                icon: "TickMark",
                disabled: (() => {
                  const allowedUserIdsToApprove = inspectionData.formFlow[
                    inspectionData.currentLevelOfForm
                  ]!.allowedUsersToApprove?.map((user) => user.id);
                  const approveBtnIsDisabled = !(
                    allowedUserIdsToApprove!.includes(userDetails!.id as ObjectIdString) && inspectionData.isSubmitted
                  );
                  return approveBtnIsDisabled;
                })(),
              },
            ]
          : [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.submitBtnTxt}`,
                id: "submit",
                icon: "TickMark",
                disabled: (() => {
                  const allowedUserIdsToSubmit =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToSubmit?.map(
                      (user) => user.id,
                    ) || [];
                  const submitBtnIsDisabled =
                    !allowedUserIdsToSubmit.includes(userDetails!.id as ObjectIdString) &&
                    !inspectionData.isArchived!.status;
                  return submitBtnIsDisabled;
                })(),
              },
            ]),
        ...(Object.hasOwn(inspectionData.formFlow[inspectionData.currentLevelOfForm]!, "allowedUsersToRollBack")
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.rollBackBtnTxt}`,
                id: "rollBack",
                icon: "RollBackIcon",
                disabled: (() => {
                  const allowedUserIdsToRollBack =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToRollBack?.map(
                      (user) => user.id,
                    ) || [];

                  const rollBackBtnIsDisabled = !allowedUserIdsToRollBack.includes(userDetails!.id as ObjectIdString);
                  return rollBackBtnIsDisabled;
                })(),
              },
            ]
          : []),
        ...(Object.hasOwn(inspectionData.formFlow[inspectionData.currentLevelOfForm]!, "allowedUsersToReject")
          ? [
              {
                text: `${inspectionData.formFlow[inspectionData.currentLevelOfForm]!.rejectBtnTxt}`,
                id: "reject",
                icon: "Reject",
                disabled: (() => {
                  const allowedUserIdsToReject =
                    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.allowedUsersToReject?.map(
                      (user) => user.id,
                    ) || [];

                  const rejectBtnIsDisabled = !allowedUserIdsToReject.includes(userDetails!.id as ObjectIdString);
                  return rejectBtnIsDisabled;
                })(),
              },
            ]
          : []),
      ]
    : [];

  const sideBarAccordions = [
    ...(contractId
      ? [
          {
            type: "contractDetails",
            heading: t("Contract Details"),
            iconName: "details",
          },
        ]
      : []),
    { type: "details", heading: t("Details"), iconName: "details" },
    { type: "formFlow", heading: t("Form Flow"), iconName: "activity" },
    { type: "access", heading: t("Access"), iconName: "access" },
    { type: "issues", heading: t("Issues"), iconName: "IssuesIcon" },
  ];

  const fetchFormData = async () => {
    const res = (await getForm({ resourceId })) as FormsControllerResult;
    let data: FormInstance = {} as FormInstance;
    if (_.get(res, "data.status", false)) {
      data = res!.data.result![0]!;
      if (data) {
        let modifiedData = {
          ...data,
        };
        if (portalData?.frontendUrl?.includes("rustomjee") && data.resourceType == formCategories.bills) {
          const template = data.selectedForm.template?.map((section) => {
            const allQuestions = section.questionFields?.map((question) => {
              if (question.fieldLabel == "MBOOK_NO" && data.MBOOK_NO) {
                question.answer = {
                  value: data.MBOOK_NO,
                };
              }
              if (question.fieldLabel == "SES_NO" && data.SES_NO) {
                question.answer = {
                  value: data.SES_NO,
                };
              }
              return question;
            });
            section.questionFields = allQuestions;
            return section;
          });

          modifiedData = {
            ...data,
            selectedForm: {
              ...data.selectedForm,
              template,
            },
          };
        }
        setInspectionData(() => ({ ...modifiedData }));
      }
    }
    setIsDataFetched(true);
    return data;
  };
  const setInitialData = async () => {
    const data = await fetchFormData();
    if (data._id) {
      axiosWrapper!.post<RecentAccessResponse>(`${getBackendUrl()}/recentAccess/recentAccess`, {
        module: data.resourceType,
        metaData: {
          _id: resourceId,
          name: data.title,
          projectId: data.projectId,
          resourceType: data.resourceType,
        },
        projectId: data.projectId,
        platform: mobile ? PLATFORM.mobile : PLATFORM.web,
      });
      if (!mobile) {
        const allSectionsInCurrentLevel: Array<string> = [];
        data.formFlow[data.currentLevelOfForm]!.dataSource!.map((data) => {
          data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id!));
        });
        handleExpandSelectedSections(allSectionsInCurrentLevel);
      }
    }
    if (data.contractId) {
      dispatch(getContractDetails({ contractId: data.contractId }));
      dispatch(getCurrencyData!({ projectId: params!.projectId as ObjectIdString }));
    }
  };
  useEffect(() => {
    if (resourceId) {
      setInitialData();
    }
  }, []);

  async function getLocationForMobile() {
    const teamRawResponse = await axiosWrapper!.post<LocationControllerResponse>(
      `${getBackendUrl()}/projects/locationController`,
      {
        value: { params: { projectId }, projectId },
      },
    );
    let teamResponse = teamRawResponse.data.result;
    return teamResponse;
  }

  async function getTeamDataForMobile() {
    const locationRawResponse = await axiosWrapper!.post<TeamsControllerResponse>(
      `${getBackendUrl()}/projects/teamsController`,
      {
        value: { params: { projectId }, projectId },
      },
    );
    let locationResponse = locationRawResponse.data.result;
    return locationResponse;
  }

  const handleChangeAnswer = ({
    source,
    answerValue,
    fieldIndex,
    sectionIndex,
    tableRowIndex,
    questionId,
  }: {
    source?: string;
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    tableRowIndex?: number;
    questionId?: string;
  }) => {
    startTransition(() => {
      if (source == formSectionType.FORM) {
        setInspectionData((prev) => {
          const modifiedTemplate = [...prev.selectedForm.template];
          const questionField = modifiedTemplate![sectionIndex!]?.questionFields![fieldIndex!];
          if (questionField) {
            questionField.answer = questionField.answer
              ? { ...questionField.answer, value: answerValue }
              : { value: answerValue };
          }
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      } else {
        setInspectionData((prev) => {
          const modifiedTemplate = [...prev.selectedForm.template];
          const section = modifiedTemplate![sectionIndex!];
          if (section) {
            section.tableDataSource = section.tableDataSource!.map((row: TableRow, index: number) => {
              if (index == tableRowIndex) {
                row[questionId!] = {
                  createdDate: Date.now(),
                  createdBy: toObjectIdString(userDetails!._id),
                  value: answerValue,
                };
              }
              return row;
            });
          }
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      }
    });
  };

  function getMultiSelectValue(args: unknown, currentAnswer: { value?: unknown[] } | undefined, viewType: string) {
    if (viewType === optionsViewType.LIST) {
      const prevAns = currentAnswer?.value || [];
      return (args as { checked?: boolean }).checked
        ? [...prevAns, (args as { value?: unknown }).value]
        : prevAns.filter((ans) => ans !== (args as { value?: unknown }).value);
    }

    if (viewType === optionsViewType.DROPDOWN) {
      return args || [];
    }

    return [];
  }

  const handleMobile = ({
    args,
    answerValue,
    sectionIndex,
    eachField,
    section,
    source,
    tableRowIndex,
    fieldIndex,
  }: {
    args: unknown;
    source: string;
    answerValue: unknown;
    fieldIndex: number;
    sectionIndex: number;
    tableRowIndex: number;
    eachField: QuestionField;
    section: FormInstanceSection;
  }) => {
    const { _id, fieldType, viewType = optionsViewType.LIST } = eachField;
    let currentAnswer = (eachField.answer ?? {}) as { value?: unknown[] };

    if (source === formSectionType.TABLE_SECTION) {
      currentAnswer = (section.tableDataSource![tableRowIndex]?.[_id] ?? {}) as { value?: unknown[] };
    }

    switch (fieldType) {
      case "single-line":
      case "multi-line":
      case "single-select":
        answerValue = args;
        break;

      case "multi-select":
        answerValue = getMultiSelectValue(args, currentAnswer, viewType);
        break;

      case "date-time":
        answerValue = (args as Date).getTime();
        break;

      case "look-up":
        answerValue = args;
        break;

      case "number":
        answerValue = Number((args as string).replace(/\D/g, ""));
        break;

      case "geoLocation":
        answerValue = args;
        break;

      default:
        break;
    }

    handleChangeAnswer({
      source,
      answerValue,
      fieldIndex,
      sectionIndex,
      tableRowIndex,
      questionId: _id,
    });

    return answerValue;
  };

  const handleInputChange = async ({
    args,
    answerValue,
    sectionIndex,
    eachField,
    section,
    source = "",
    tableRowIndex,
    fieldIndex,
  }: HandleInputChangeArgs) => {
    const { _id } = eachField as QuestionField;

    const questionId = _id;
    const sectionId = section!._id;
    if (mobile) {
      answerValue = handleMobile({
        args,
        answerValue,
        sectionIndex: sectionIndex!,
        eachField: eachField as QuestionField,
        section: section as FormInstanceSection,
        source: source!,
        tableRowIndex: tableRowIndex!,
        fieldIndex: fieldIndex!,
      });
    }
    if (!([undefined, NaN] as unknown[]).includes(answerValue)) {
      if (source == formSectionType.FORM) {
        setEachAnswerToSave({
          sectionId,
          questionId,
          answer: { value: answerValue },
        });
      } else {
        const modifiedTemplate = _.cloneDeep(inspectionData.selectedForm.template);
        const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == tableRowIndex) {
              row[_id] = {
                createdDate: Date.now(),
                createdBy: toObjectIdString(userDetails!._id),
                value: answerValue,
              };
            }
            return row;
          },
        );
        setEachAnswerToSave({
          tableSectionId: sectionId,
          tableSectionDataSource: modifiedTableDataSource,
        });
      }
    }
  };

  const validateMaxFiles = (eachField: QuestionField, prevAns: unknown[]) => {
    if (eachField.maxNoOfFiles && prevAns && prevAns.length >= eachField.maxNoOfFiles) {
      dispatch(
        showToastNotification({
          content: t("Max only {{count}} files can be uploaded", { count: eachField.maxNoOfFiles }) as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }
    return true;
  };

  const validateFileType = (file: UploadCandidate, eachField: QuestionField, allowedFileTypes: string[]) => {
    if (
      (eachField.fieldType === "image" && !allowedFileTypes.includes(file.type!)) ||
      (eachField.fieldType === "fileUpload" && allowedFileTypes.length && !allowedFileTypes.includes(file.type!))
    ) {
      dispatch(
        showToastNotification({
          content: t("File type not allowed") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }
    return true;
  };

  const validateFileSize = (file: UploadCandidate, e: unknown) => {
    if (file.size! > MAX_FORM_ANSWER_FILE_SIZE) {
      const msg = t("File size exceeds the limit. Max file size allowed is {{max, number}} MB", {
        max: MAX_FORM_ANSWER_FILE_SIZE / (1024 * 1024),
      });
      if (mobile) {
        dispatch(showToastNotification({ content: msg as TranslatedString, type: TOAST_TYPE_WARNING }));
      } else {
        alert(msg);
        (e as FileInputEvent).target.value = "";
      }
      return false;
    }
    return true;
  };

  const updateFormTemplate = (
    args: FileUploadChangeArgs,
    addedFile?: FieldFileValue,
    baseTemplate?: NonNullable<FileUploadChangeArgs["formData"]["selectedForm"]>["template"],
  ) => {
    const { formData, sectionIndex, fieldIndex, sectionId, eachField, tableRowIndex, source } = args;
    const questionId = eachField._id;
    const modifiedTemplate = baseTemplate ?? _.cloneDeep(formData!.selectedForm!.template!);
    const addedEntries: unknown[] = addedFile ? [addedFile] : [];
    let answerValue: unknown[] = addedEntries;

    if (source === formSectionType.FORM) {
      if (eachField.answer?.value) {
        answerValue = [...(eachField.answer.value as unknown[]), ...addedEntries];
      }
      modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = { value: answerValue };

      return {
        template: modifiedTemplate,
        data: {
          addFileUploadFieldData: {},
          answer: { value: answerValue },
          sectionId,
          questionId,
        },
      };
    }

    const prevAns = readFileList(
      readTableRow(formData!.selectedForm!.template![sectionIndex!]!.tableDataSource![tableRowIndex])[questionId]
        ?.value,
    );
    if (prevAns) answerValue = [...prevAns, ...addedEntries];

    modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
      (row: unknown, i: number) =>
        i === tableRowIndex
          ? {
              ...readTableRow(row),
              [questionId]: { createdBy: userDetails!._id, createdDate: Date.now(), value: answerValue },
            }
          : row,
    );

    return {
      template: modifiedTemplate,
      data: {
        tableSectionId: sectionId,
        questionId,
        tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        addFileUploadFieldData: { tableRowIndex },
      },
    };
  };

  const base64ToFile = (base64EncodedImg: string) => {
    const [meta = "", base64Data = ""] = base64EncodedImg.split(",");
    const mimeType = meta.match(/data:(.*?);base64/)?.[1] || "image/jpeg";
    const byteString = atob(base64Data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    const extension = mimeType.split("/")[1] || "jpeg";
    return new File([byteArray], `${uuid!()}.${extension}`, { type: mimeType });
  };

  const handleFileUploadInTemplate = async (file: File | string) => {
    const fileToUpload = typeof file === "string" && file.startsWith("data:") ? base64ToFile(file) : (file as File);

    const formDataForFile = new FormData();
    formDataForFile.append("uploadFiles", fileToUpload);
    const response = await axiosWrapper.post<{ status: boolean; uuid?: string; errorMessage?: string }>(
      `${getBackendUrl()}/forms/uploadFilesInTemplate`,
      formDataForFile,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    if (!response?.data?.status || !response.data.uuid) return;
    return response.data.uuid;
  };
  const handleFileUploadChange = async (args: FileUploadChangeArgs) => {
    const {
      e,
      eachField,
      sectionIndex,
      tableRowIndex,
      source,
      tableRowId = null,
      formData = {} as FormInstance,
      handleCustomFieldData,
      fieldIndex,
    } = args;

    const questionId = eachField._id;
    const allowedFileTypes = eachField.allowedFileTypes || [];
    let prevAns = (eachField.answer?.value as unknown[]) || [];

    if (source === formSectionType.TABLE_SECTION) {
      prevAns =
        readFileList(
          readTableRow(formData.selectedForm!.template![sectionIndex!]!.tableDataSource![tableRowIndex])[questionId]
            ?.value,
        ) || [];
    }

    if (!validateMaxFiles(eachField, prevAns)) return;
    const file = (mobile ? e : (e as FileInputEvent).target.files?.[0]) as UploadCandidate | undefined;
    if (!file) return;
    if (!validateFileType(file, eachField, allowedFileTypes)) return;
    if (!validateFileSize(file, e)) return;

    if (!mobile && !(file instanceof File)) {
      dispatch(showToastNotification({ content: t("Invalid file") as TranslatedString, type: TOAST_TYPE_WARNING }));
      return;
    }
    const candidate = file as UploadCandidate;
    const mobileUri = candidate.uri;
    if (mobile && !mobileUri) {
      dispatch(showToastNotification({ content: t("Invalid file") as TranslatedString, type: TOAST_TYPE_WARNING }));
      return;
    }
    const fileMeta: FieldFileValue = { type: file.type, name: file.name || candidate.fileName };
    const filePart: MultipartFile = mobile
      ? { uri: mobileUri ?? "", name: fileMeta.name || "", type: file.type }
      : (file as File);

    const templateDraft = _.cloneDeep(formData.selectedForm!.template!);
    const { data } = updateFormTemplate(args, undefined, templateDraft);

    const uniqueId = tableRowId ? `${tableRowId}${questionId}` : questionId;

    setIsFileUploading((prev) => [...prev, { type: eachField.fieldType, uniqueId }]);

    const existingInFlight = inFlightUploadsRef.current[uniqueId];
    if (existingInFlight) {
      existingInFlight.count += 1;
    } else {
      inFlightUploadsRef.current[uniqueId] = { count: 1, value: prevAns };
    }

    try {
      if (handleCustomFieldData) {
        const UUID = await handleFileUploadInTemplate(file as File);
        if (UUID) {
          const current = inFlightUploadsRef.current[uniqueId];
          const base = current ? current.value : prevAns;
          const answerValue = [...base, { ...fileMeta, awsKey: UUID }];
          if (current) current.value = answerValue;
          (handleCustomFieldData as HandleCustomFieldData)({
            eachField,
            fieldIndex,
            answerValue,
            sectionIndex,
            tableRowIndex,
            source,
          });
        }
      } else if (inspectionData._id) {
        const res = (await updateTaskInspectionAndProgressWithFile(
          { action: "update", _id: toObjectIdString(resourceId), data },
          filePart,
          fileMeta.name || "",
        )) as FormsControllerResult;
        if (res.data.status) {
          setAnswerSaved(true);
          if (!mobile) setTimeout(() => setAnswerSaved(false), 500);
          dispatch(
            showToastNotification({ content: t("File uploaded") as TranslatedString, type: TOAST_TYPE_SUCCESS }),
          );
          const { template } = updateFormTemplate(args, res.data.addedFileData, templateDraft);
          setInspectionData(
            (prev) =>
              ({
                ...prev,
                lastSaved: res.data.lastSaved,
                selectedForm: { ...prev.selectedForm, template },
              }) as FormInstance,
          );
        } else {
          dispatch(showToastNotification({ content: untranslated(res.data.errorMessage), type: TOAST_TYPE_ERROR }));
        }
      }
    } finally {
      const current = inFlightUploadsRef.current[uniqueId];
      if (current) {
        current.count -= 1;
        if (current.count <= 0) delete inFlightUploadsRef.current[uniqueId];
      }
      setIsFileUploading((prev) => prev.filter((d) => d.uniqueId !== uniqueId));
    }
  };

  const saveEditedImage = async ({
    base64EncodedImg,
    awsKey,
    handleCustomFieldData,
    sectionId,
    questionId,
    eachField,
    fieldIndex,
    sectionIndex,
    tableRowIndex,
    source,
    imageIndex,
    currentAnswer,
  }: SaveEditedImageArgs) => {
    if (handleCustomFieldData) {
      const UUID = await handleFileUploadInTemplate(base64EncodedImg);
      if (UUID) {
        const updateAns = (currentAnswer?.value ?? []).map((image, index: number) =>
          index === imageIndex ? { ...image, awsKey: UUID } : image,
        );
        (handleCustomFieldData as HandleCustomFieldData)({
          eachField,
          fieldIndex,
          answerValue: updateAns,
          sectionIndex,
          tableRowIndex,
          source,
        });
      }
    } else if (inspectionData._id) {
      const editedFile = base64ToFile(base64EncodedImg);
      if (!awsKey) return;
      setEachAnswerToSave({
        addFileUploadFieldData: { awsKey, tableRowIndex },
        sectionId,
        questionId,
        uploadFile: { file: editedFile, name: editedFile.name },
      });
    }
  };

  const handleRemoveUploadedFile = ({
    fileData,
    sectionIndex,
    fieldIndex,
    sectionId,
    questionId,
    source,
    tableRowIndex,
    eachField,
    handleCustomFieldData,
    currentAnswer,
  }: RemoveUploadedFileArgs) => {
    const { awsKey, fileVersion } = fileData;
    const matchingValue = awsKey || fileVersion;
    const matchingKey = awsKey ? "awsKey" : "fileVersion";

    if (handleCustomFieldData) {
      const modifiedAnswer = (currentAnswer?.value ?? []).filter(
        (data: FieldFileValue) => data[matchingKey] != matchingValue,
      );
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField,
        fieldIndex,
        answerValue: modifiedAnswer,
        sectionIndex,
        tableRowIndex,
        source,
      });
    } else if (inspectionData._id) {
      const modifiedTemplate = [...inspectionData.selectedForm.template];
      if (source == formSectionType.FORM) {
        const storedFiles = (eachField as { answer?: { value?: Array<Record<string, unknown>> } } | undefined)?.answer
          ?.value;
        if (!storedFiles) return;
        const modifiedAnswer = storedFiles.filter((data) => data[matchingKey] != matchingValue);

        modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
          ...modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer,
          value: modifiedAnswer,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        const payload: EachAnswerToSave = {
          answer: { value: modifiedAnswer },
          sectionId,
          questionId,
        };
        if (awsKey) {
          payload.removeFileUploadFieldData = { awsKey: [awsKey] };
        }
        setEachAnswerToSave(payload);
      } else {
        const storedFiles = readFileList(
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex]![questionId]!.value,
        );
        const modifiedAnswer = storedFiles!.filter((data) => data[matchingKey] != matchingValue);

        modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == tableRowIndex) {
              row[questionId] = {
                createdBy: toObjectIdString(userDetails!._id),
                createdDate: Date.now(),
                value: modifiedAnswer,
              };
            }
            return row;
          },
        );

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        const payload: EachAnswerToSave = {
          tableSectionId: sectionId,
          questionId,
          tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        };
        if (awsKey) {
          payload.removeFileUploadFieldData = { tableRowIndex, awsKey: [awsKey] };
        }
        setEachAnswerToSave(payload);
      }
    }
  };

  const handleRemoveAllUploadedFiles = ({
    sectionIndex,
    fieldIndex,
    sectionId,
    questionId,
    source,
    tableRowIndex,
    eachField,
    handleCustomFieldData,
  }: RemoveAllUploadedFilesArgs) => {
    if (handleCustomFieldData) {
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField,
        fieldIndex,
        answerValue: [],
        sectionIndex,
        tableRowIndex,
        source,
      });
      return;
    }
    if (!inspectionData._id) return;
    const storedFiles =
      source == formSectionType.FORM
        ? ((eachField as { answer?: { value?: FieldFileValue[] } }).answer?.value ?? [])
        : ((inspectionData.selectedForm.template[sectionIndex!]?.tableDataSource?.[tableRowIndex]?.[questionId]
            ?.value as FieldFileValue[] | undefined) ?? []);
    if (!storedFiles.length) return;
    const awsKeys = storedFiles.map((file) => file.awsKey).filter(Boolean) as string[];

    const modifiedTemplate = [...inspectionData.selectedForm.template];
    const payload: EachAnswerToSave = {};

    if (source == formSectionType.FORM) {
      modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
        ...modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer,
        value: [],
      };
      payload.answer = { value: [] };
      payload.sectionId = sectionId;
      payload.questionId = questionId;
    } else {
      modifiedTemplate[sectionIndex!]!.tableDataSource = modifiedTemplate[sectionIndex!]!.tableDataSource!.map(
        (row: TableRow, index: number) =>
          index == tableRowIndex
            ? {
                ...row,
                [questionId]: { createdBy: toObjectIdString(userDetails!._id), createdDate: Date.now(), value: [] },
              }
            : row,
      );
      payload.tableSectionId = sectionId;
      payload.questionId = questionId;
      payload.tableSectionDataSource = [...modifiedTemplate[sectionIndex!]!.tableDataSource!];
    }

    if (awsKeys.length) {
      payload.removeFileUploadFieldData =
        source == formSectionType.FORM ? { awsKey: awsKeys } : { tableRowIndex, awsKey: awsKeys };
    }

    setInspectionData((prev) => ({
      ...prev,
      selectedForm: { ...prev.selectedForm, template: modifiedTemplate },
    }));
    setEachAnswerToSave(payload);
  };

  const manageAbortControllers = (questionId: string | undefined, abortController: AbortController) => {
    if (!questionId) return;

    const existingControllers = _.get(abortControllerList, `current.${questionId}`) as AbortController[] | undefined;
    if (existingControllers) {
      existingControllers.forEach((c) => c.abort());
      abortControllerList.current[questionId] = [];
    }

    if (abortControllerList.current[questionId]) {
      abortControllerList.current[questionId].push(abortController);
    } else {
      abortControllerList.current[questionId] = [abortController];
    }
  };
  const updateAttachments = (
    res: FormsControllerResult,
    eachAnswerToSave: EachAnswerToSave,
    setInspectionData: React.Dispatch<React.SetStateAction<FormInstance>>,
  ) => {
    if (!res.data.addedAttachmentData && !res.data.autoFieldAnswers) return;

    setInspectionData((prev) => {
      const { addedAttachmentData, autoFieldAnswers, lastSaved } = res.data;

      const { sectionIndex, fieldIndex } = eachAnswerToSave;
      const attachmentObj = addedAttachmentData?.obj;

      const newTemplate = prev.selectedForm.template.map((section: FormInstanceSection, sIdx: number) => {
        if (!section.questionFields) return section;

        let sectionChanged = false;

        const newQuestionFields = section.questionFields!.map((field: QuestionField, fIdx: number) => {
          let updated = field;

          if (attachmentObj && sIdx === sectionIndex && fIdx === fieldIndex) {
            updated = {
              ...updated,
              attachmentsData: [...(updated.attachmentsData ?? []), { ...attachmentObj }],
            };
          }

          if (
            autoFieldAnswers &&
            updated._id != null &&
            Object.prototype.hasOwnProperty.call(autoFieldAnswers, updated._id)
          ) {
            updated = {
              ...updated,
              answer: { ...updated.answer, value: autoFieldAnswers[updated._id] },
            };
          }

          if (updated !== field) sectionChanged = true;
          return updated;
        });

        return sectionChanged ? { ...section, questionFields: newQuestionFields } : section;
      });

      return {
        ...prev,
        lastSaved,
        answerMap: autoFieldAnswers ? { ...prev.answerMap, ...autoFieldAnswers } : prev.answerMap,
        selectedForm: { ...prev.selectedForm, template: newTemplate },
      } as FormInstance;
    });
  };
  const setEachAnswer = async () => {
    if (!Object.keys(eachAnswerToSave).length) return;

    const abortController = new AbortController();
    const questionId = _.get(eachAnswerToSave, "questionId") || _.get(eachAnswerToSave, "tableSectionId");

    manageAbortControllers(questionId, abortController);

    try {
      const { uploadFile, sectionIndex: _sectionIndex, fieldIndex: _fieldIndex, ...data } = eachAnswerToSave;
      const res = (
        uploadFile
          ? await updateTaskInspectionAndProgressWithFile(
              { action: "update", _id: toObjectIdString(resourceId), data },
              uploadFile.file,
              uploadFile.name,
              abortController.signal,
            )
          : await updateForm({ action: "update", _id: resourceId, data }, abortController)
      ) as FormsControllerResult;

      updateAttachments(res, eachAnswerToSave, setInspectionData);

      if (res.data.status) {
        setAnswerSaved(true);
        setTimeout(() => setAnswerSaved(false), 500);
        setLastSaved(res.data.lastSaved);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      Promise.reject(error);
    }
  };

  useEffect(() => {
    setEachAnswer();
  }, [eachAnswerToSave]);

  useEffect(() => {
    if (inspectionData?._id && isDataFetched) {
      addValidationToForms({
        formValidator,
        inspectionData,
        modifySection,
      });
    }
  }, [inspectionData?._id, inspectionData?.currentLevelOfForm, modifySection, isDataFetched]);

  const openAddUserModal = ({
    eachField,
    fieldIndex,
    sectionIndex,
    section,
    source = formSectionType.FORM,
    tableRowIndex,
    handleCustomFieldData,
    tableRowId,
  }: OpenAddUserModalArgs) => {
    if (eachField.onlyCurrentUser) {
      const currentUser = {
        _id: userDetails!.id,
        signature: userDetails!.signature,
        firstName: userDetails!.firstName,
        fullName: userDetails!.fullName,
        lastName: userDetails!.lastName,
      };
      if (handleCustomFieldData) {
        (handleCustomFieldData as HandleCustomFieldData)({
          eachField,
          answerValue: currentUser,
          fieldIndex,
          sectionIndex,
          tableRowIndex,
          source,
        });
      } else if (inspectionData._id) {
        const modifiedTemplate = [...inspectionData.selectedForm.template];
        if (source == formSectionType.FORM) {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: [currentUser],
          };
          setInspectionData((prev) => ({
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          }));

          setEachAnswerToSave({
            sectionId: section!._id,
            questionId: eachField!._id,
            answer: { value: [currentUser] },
          });
        } else {
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!] = {
            createdBy: toObjectIdString(userDetails!._id),
            createdDate: Date.now(),
            value: [currentUser],
          };
          setInspectionData((prev) => ({
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          }));

          setEachAnswerToSave({
            tableSectionId: section!._id,
            tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
          });
        }
        if (mobile) {
          dispatch(
            showToastNotification({
              content: t("User Added") as TranslatedString,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }
      }
      return;
    }
    setShowAddUsersModal({
      status: true,
      fieldData: { ...eachField },
      fieldIndex,
      sectionIndex,
      section,
      source,
      tableRowIndex,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    });
    bottomSheetRefUsers?.current?.present();
  };

  const closeUsersModal = () => {
    setShowAddUsersModal({ status: false } as AddUsersModalState);
  };

  const removeUserFromList = ({
    fieldIndex,
    sectionIndex,
    user,
    eachField,
    section,
    source = formSectionType.FORM,
    tableRowIndex,
    handleCustomFieldData,
  }: RemoveUserFromListArgs) => {
    if (handleCustomFieldData) {
      const allUsers = eachField!.answer!.value as Array<{ _id?: string }>;
      const filteredUsers = allUsers.filter((data) => data._id != user._id);
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField,
        answerValue: filteredUsers,
        fieldIndex,
        sectionIndex,
        tableRowIndex,
        source,
      });
    } else if (inspectionData._id) {
      const modifiedTemplate = [...inspectionData.selectedForm.template];
      if (source == formSectionType.FORM) {
        const allUsers = modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer!.value as Array<
          Record<string, unknown>
        >;
        const filteredUsers = allUsers.filter((data) => data._id != user._id);

        modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
          value: filteredUsers,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          sectionId: section!._id,
          questionId: eachField!._id,
          answer: { value: filteredUsers },
        });

        mobile &&
          setShowAddUsersModal({
            ...showAddUsersModal,
            status: true,
            fieldData: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!,
            fieldIndex,
            sectionIndex,
            section: section as AddUsersModalState["section"],
            source,
            tableRowIndex,
          });
      } else {
        const allUsers = readUserList(
          modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!]!.value,
        );
        const filteredUsers = allUsers!.filter((data) => data._id != user._id);

        modifiedTemplate![sectionIndex!]!.tableDataSource![tableRowIndex!]![eachField!._id!] = {
          createdBy: toObjectIdString(userDetails!._id),
          createdDate: Date.now(),
          value: filteredUsers,
        };

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          tableSectionId: section!._id,
          tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
        });
        mobile &&
          setShowAddUsersModal({
            ...showAddUsersModal,
            status: true,
            fieldData: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!,
            fieldIndex,
            sectionIndex,
            section: section as AddUsersModalState["section"],
            source,
            tableRowIndex,
          });
      }
    }
  };

  const openAddedUserModalInMobile = ({
    eachField,
    fieldIndex,
    sectionIndex,
    showCrossIcon = false,
    section,
    source,
    tableRowIndex,
  }: {
    eachField: QuestionField;
    fieldIndex: number;
    sectionIndex: number;
    showCrossIcon: boolean;
    section: FormInstanceSection;
    source: string;
    tableRowIndex: number;
  }) => {
    setShowAddUsersModal({
      status: true,
      fieldData: { ...eachField },
      fieldIndex,
      sectionIndex,
      showCrossIcon,
      section,
      source,
      tableRowIndex,
    } as AddUsersModalState);
    bottomSheetRefAddedUsers?.current?.present();
  };

  const verifySection = (sectionIds: string[]) => {
    let uniqueSectionIds: Set<string> | string[] = new Set([...sectionIds, ...inspectionData.sectionsVerified]);
    uniqueSectionIds = Array.from(uniqueSectionIds);
    setInspectionData((prev) => ({
      ...prev,
      sectionsVerified: uniqueSectionIds,
    }));
    setEachAnswerToSave({ sectionsVerified: [...uniqueSectionIds] });
  };

  function handleAttachmentClick(sectionIndex: number, fieldIndex: number) {
    setShowAttachmentModal(true);
    setAttachmentModalOpenedFor({
      sectionIndex,
      fieldIndex,
    });
    bottomSheetRefFiles?.current?.present();
  }

  const closeAttachmentModal = () => {
    setShowAttachmentModal(false);
  };

  const handleModifyCancel = (event: { stopPropagation: () => void } | undefined, sectionId: string) => {
    event?.stopPropagation();
    setModifySection((prev) => prev.filter((id) => id != sectionId));
  };

  const verifyToggleStatus = (section: FormInstanceSection) => {
    const allowedUsersToVerifyPostSub = section.permissions![inspectionData.currentLevelOfForm]!.Verify;
    return !(allowedUsersToVerifyPostSub.includes(userDetails!.id!) && inspectionData.isSubmitted);
  };

  const saveInspectionData = async (type: string) => {
    const data: TipfUpdateRequest["data"] = {};
    if (type == "submit") {
      const { editedLevel, levelIndex } = levelEditModal;
      if (editedLevel && levelIndex !== null) {
        data.editedLevel = editedLevel;
      }
      if (inspectionData.currentLevelOfForm == inspectionData.formFlow.length - 2) {
        data.isSubmitted = true;
      } else {
        data.isSubmitted = false;
        data.proceedToNextLevel = true;
      }
    } else if (type == "approve") {
      data.isApproved = true;
    } else if (type == "reject") {
      data.isRejected = true;
    } else if (type == "rollback") {
      data.rollBack = true;
    }
    if (["approve", "reject", "rollback"].includes(type)) {
      data.actionComment = formActionDetails.comment;
      data.actionSignDoc = formActionDetails.signDoc;
      data.actionSignType = formActionDetails.signType;
    }
    setActionBtnDisabled(true);
    const res = (await updateForm({
      action: "update",
      _id: resourceId,
      data,
    })) as FormsControllerResult;
    setActionBtnDisabled(false);
    return res;
  };

  const handleRollBack = async () => {
    setShowWaitSpinner(true);
    setActionBtnDisabled(true);
    const res = await saveInspectionData("rollback");
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Form Rolled back to previous level"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      const updatedFormFLow = [...inspectionData.formFlow];
      updatedFormFLow[inspectionData.currentLevelOfForm - 1]!.actionStatus = false;
      updatedFormFLow[inspectionData.currentLevelOfForm - 1]!.actionDetails = {};
      await fetchFormData();
      closeCommentDialog();
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      setShowWaitSpinner(false);
    }
    setActionBtnDisabled(false);
  };

  const handleFormActionClick = (args: { item: { id?: string } }) => {
    if (args.item.id == "approve") {
      if (inspectionData.sectionsVerified?.length != inspectionData.selectedForm.template.length) {
        dispatch(
          showToastNotification({
            content: t("Please verify all sections before approving the form") as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        setOpenCommentDialog({ status: true, heading: "Approve", btnText: "Approve", source: "approve" });
      }
    }
    if (args.item.id == "reject") {
      setOpenCommentDialog({ status: true, heading: "Reject", btnText: "Reject", source: "reject" });
    }
    if (args.item.id == "submit") {
      handleSubmitClick();
    }
    if (args.item.id == "rollBack") {
      setOpenCommentDialog({ status: true, heading: "Roll Back", btnText: "Roll Back", source: "rollBack" });
    }
  };

  const beforeOpenActionDropdown = (args: { items?: Array<{ id?: string; disabled?: boolean }> }) => {
    const items = args?.items;
    if (!items?.length) return;

    const currentFlow = inspectionData.formFlow?.[inspectionData.currentLevelOfForm];
    if (!currentFlow) return;

    const findItem = (id: string) => items.find((it: { id?: string; disabled?: boolean }) => it?.id === id);
    const allowedIds = (key: string) =>
      ((currentFlow[key] as Array<{ id: string }> | undefined)?.map((user) => user.id) ?? []) as string[];

    const rollBackItem = findItem("rollBack");
    if (rollBackItem && Object.hasOwn(currentFlow, "allowedUsersToRollBack")) {
      rollBackItem.disabled = !allowedIds("allowedUsersToRollBack").includes(userDetails!.id as string);
    }

    const rejectItem = findItem("reject");
    if (rejectItem && Object.hasOwn(currentFlow, "allowedUsersToReject")) {
      rejectItem.disabled = !allowedIds("allowedUsersToReject").includes(userDetails!.id as string);
    }

    if (inspectionData.isSubmitted) {
      const approveItem = findItem("approve");
      if (approveItem) {
        approveItem.disabled = !(
          allowedIds("allowedUsersToApprove").includes(userDetails!.id as string) && inspectionData.isSubmitted
        );
      }
    } else {
      const submitItem = findItem("submit");
      if (submitItem) {
        submitItem.disabled =
          !allowedIds("allowedUsersToSubmit").includes(userDetails!.id as string) && !inspectionData.isArchived?.status;
      }
    }
  };

  const handleFormStateChange = async () => {
    const res = (await archiveForms({
      action: "archive",
      resourceIds: [inspectionData._id],
      data: { isArchived: { status: true } },
    })) as FormsControllerResult;

    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Successfully Archived") as TranslatedString,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    let urlLink;
    if (inspectionData?.contractId) {
      urlLink = getContractDetailUrl!({
        selectedTab: formCategoryObject[inspectionData.resourceType]?.selectedTab ?? "list",
        projectId: params!.projectId,
        contractId: inspectionData?.contractId,
      });
    } else {
      urlLink = getFormsListUrl!({
        selectedTab: "list",
        projectId: params!.projectId,
        resourceType: inspectionData.resourceType,
      });
    }
    navigate!(urlLink);
  };

  const handleCollapseAll = () => {
    setAccordionExpandedState([]);
  };
  const handleExpandAll = (template: FormInstanceSection[] = []) => {
    const sectionIds: Array<string> = template?.map((section) => section._id!);
    setAccordionExpandedState([...sectionIds]);
  };

  const handleExpandSelectedSections = (sectionIds: Array<string>) => {
    setAccordionExpandedState((prev: Array<string>) => [...prev, ...sectionIds]);
  };

  const handleAccStateChange = (args: unknown, sectionId: string) => {
    setAccordionExpandedState((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id != sectionId);
      }

      return [...prev, sectionId];
    });
  };

  const showToastAndRedirect = (content: TranslatedString) => {
    dispatch(
      showToastNotification({
        content,
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    if (inspectionData?.contractId) {
      if (!mobile) {
        const urlLink = getContractDetailUrl!({
          selectedTab: formCategoryObject[inspectionData.resourceType]?.selectedTab ?? "list",
          projectId: params!.projectId,
          contractId: inspectionData?.contractId,
        });
        navigate!(urlLink);
      } else {
        navigation!.replace("FormsList", {
          formList: inspectionData.resourceType,
          projectId: params!.projectId,
          contractId: inspectionData?.contractId,
        });
      }
    } else if (!mobile) {
      const urlLink = getFormsListUrl!({
        selectedTab: "list",
        projectId: params!.projectId,
        resourceType: inspectionData.resourceType,
      });
      navigate!(urlLink);
    } else {
      navigation!.replace("FormsList", {
        formList: inspectionData.resourceType,
        projectId: params!.projectId,
      });
    }
  };

  const handleFormSubmission = async () => {
    const { isAllMandatoryFieldsFilled, errorToastContent } = checkMandatoryFields(inspectionData);
    if (isAllMandatoryFieldsFilled) {
      const res = await saveInspectionData("submit");
      if (res.data.status) {
        await fetchFormData();
        dispatch(
          showToastNotification({
            content: t("Form submitted successfully navigate back to see full list") as TranslatedString,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(errorToastContent),
          type: TOAST_TYPE_ERROR,
        }),
      );
      formValidator.validateAll();
    }
  };
  const handleSubmitClick = async () => {
    if (
      inspectionData.currentLevelOfForm < inspectionData.formFlow.length - 1 &&
      inspectionData.formFlow[inspectionData.currentLevelOfForm + 1]!.allowDynamicUserConfig
    ) {
      setLevelEditModal(() => ({
        open: true,
        levelIndex: inspectionData.currentLevelOfForm + 1,
      }));
    } else {
      await handleFormSubmission();
    }
  };

  const closeCommentDialog = () => {
    setOpenCommentDialog(false as never);
    setShowWaitSpinner(false);
    setFormActionDetails({
      comment: "",
      signDoc: null,
      signType: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
    });
  };

  const handleApproveClick = async () => {
    if (inspectionData.sectionsVerified?.length != inspectionData.selectedForm.template.length) {
      dispatch(
        showToastNotification({
          content: t("Please verify all sections before approving the form") as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      setShowWaitSpinner(true);
      const res = await saveInspectionData("approve");
      if (res.data.status) {
        showToastAndRedirect("Form Approved Successfully" as TranslatedString);
        setShowWaitSpinner(false);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        setShowWaitSpinner(false);
      }
    }
  };

  function verifyAllState() {
    let isVerifyAllDisabled = true;
    (_.get(inspectionData, "selectedForm.template", []) as FormInstanceSection[]).forEach((section) => {
      const secPermission = getSectionPermission({
        section,
        inspectionData,
        userId: userDetails!.id,
        type: "Verify",
      });
      if (secPermission && inspectionData.sectionsVerified.length < inspectionData.selectedForm.template.length) {
        isVerifyAllDisabled = false;
      }
    });
    return isVerifyAllDisabled;
  }

  const handleSaveCmntClick = async () => {
    if (openCommentDialog.source == "reject") {
      setShowWaitSpinner(true);
      const res = await saveInspectionData("reject");
      if (res.data.status) {
        showToastAndRedirect(res.data.message as TranslatedString);
        setShowWaitSpinner(false);
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        setShowWaitSpinner(false);
      }
    } else if (openCommentDialog.source == "approve") {
      handleApproveClick();
    } else if (openCommentDialog.source == "rollBack") {
      handleRollBack();
    }
  };

  function onClickWarningModalConfirm({
    source,
    showWarningModal,
  }: {
    source: string;
    showWarningModal: WarningModalState;
  }) {
    if (source == "verify") {
      verifySection([showWarningModal.data!._id!]);
    }
    if (source == "modify") {
      setModifySection((prev) => [...prev, showWarningModal.data!._id!]);
    }
    if (source == "verifyAll") {
      const tempSectionIds: string[] = [];
      inspectionData.selectedForm.template?.map((section) => {
        const permission = getSectionPermission({
          section,
          inspectionData,
          userId: userDetails!.id,
          type: "Verify",
        });
        if (permission) {
          tempSectionIds.push(section._id!);
        }
      });
      verifySection([...tempSectionIds]);
    }
    hideWarningModal();
  }

  const handleShowWarningModal = (
    event: { stopPropagation: () => void } | undefined,
    source: string,
    section?: FormInstanceSection | null,
  ) => {
    event?.stopPropagation();
    setShowWarningModal({ status: true, source, data: section ?? undefined });
  };
  const hideWarningModal = () => {
    setShowWarningModal({ status: false });
  };

  const handleExportPdf = async () => {
    const formData = {
      formId: inspectionData._id,
      projectId: inspectionData.project._id,
      paperSize: openExportModal.paperSize,
      tableSectionFormat: openExportModal.tableFormat,
    };
    setOpenExportModal({ state: false, paperSize: "A4", tableFormat: "card" });
    exportPdf({
      formId: formData.formId,
      projectId: formData.projectId,
      paperSize: formData.paperSize,
      tableSectionFormat: formData.tableSectionFormat,
      exportType: EXPORT_TYPE.PDF,
    });
  };

  const handleAddTableRowClick = ({
    section,
    sectionIndex,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
  }) => {
    const newObjRow: TableRow = {};
    newObjRow.rowId = uuid!();
    inspectionData.selectedForm.template[sectionIndex!]!.questionFields!.map((field) => {
      newObjRow[field._id] = {};
    });
    const modifiedTemplate = [...inspectionData.selectedForm.template];

    modifiedTemplate![sectionIndex!]!.tableDataSource = [
      ...modifiedTemplate[sectionIndex!]!.tableDataSource!,
      newObjRow,
    ];

    setInspectionData((prev) => ({
      ...prev,
      selectedForm: {
        ...prev.selectedForm,
        template: modifiedTemplate,
      },
    }));

    setEachAnswerToSave({
      tableSectionId: section!._id,
      tableSectionDataSource: [...modifiedTemplate[sectionIndex!]!.tableDataSource!],
    });
  };

  const handleDeleteTableRow = ({
    section,
    sectionIndex,
    rowIndex,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
    rowIndex: number;
  }) => {
    const modifiedTemplate = [...inspectionData.selectedForm.template];

    const dataSource = modifiedTemplate![sectionIndex!]!.tableDataSource;

    dataSource!.splice(rowIndex, 1);

    modifiedTemplate![sectionIndex!]!.tableDataSource = [...dataSource!];

    setInspectionData((prev) => ({
      ...prev,
      selectedForm: {
        ...prev.selectedForm,
        template: modifiedTemplate,
      },
    }));

    setEachAnswerToSave({
      tableSectionId: section!._id,
      tableSectionDataSource: [...dataSource!],
    });
  };

  function handleAddUserClick({
    preSelectedUsers,
    handleCustomFieldData,
  }: {
    preSelectedUsers: unknown[];
    handleCustomFieldData?: unknown;
  }) {
    const selectedUsers = [...preSelectedUsers];
    if (inspectionData._id) {
      const sectionIndex = showAddUsersModal.sectionIndex;
      const fieldIndex = showAddUsersModal.fieldIndex;
      if (showAddUsersModal.source == formSectionType.FORM) {
        const modifiedTemplate = [...inspectionData.selectedForm.template];
        if (modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer) {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            ...modifiedTemplate[sectionIndex!]!.questionFields![fieldIndex!]!.answer,
            value: selectedUsers,
          };
        } else {
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: selectedUsers,
          };
        }

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          sectionId: modifiedTemplate![sectionIndex!]!._id,
          questionId: modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!._id,
          answer: { value: selectedUsers },
        });
      } else {
        const modifiedTemplate = [...inspectionData.selectedForm.template];

        const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
          (row: TableRow, index: number) => {
            if (index == showAddUsersModal.tableRowIndex) {
              row[showAddUsersModal.fieldData._id!] = {
                createdDate: Date.now(),
                createdBy: toObjectIdString(userDetails!._id),
                value: selectedUsers,
              };
            }
            return row;
          },
        );

        modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTableDataSource;

        setInspectionData((prev) => ({
          ...prev,
          selectedForm: {
            ...prev.selectedForm,
            template: modifiedTemplate,
          },
        }));

        setEachAnswerToSave({
          tableSectionId: modifiedTemplate![sectionIndex!]!._id,
          tableSectionDataSource: modifiedTableDataSource,
        });
      }
    } else if (handleCustomFieldData) {
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField: showAddUsersModal.fieldData,
        answerValue: selectedUsers,
        fieldIndex: showAddUsersModal.fieldIndex,
        sectionIndex: showAddUsersModal.sectionIndex,
        tableRowIndex: showAddUsersModal.tableRowIndex,
        source: showAddUsersModal.source,
      });
    }
  }
  const handleURLModalState = ({
    state,
    sectionIndex = 0,
    eachField = 0 as never,
    tableRowIndex = 0,
    answer = {},
    fieldIndex = 0,
    sectionId,
    source,
    tableRowId,
  }: FieldModalStateArgs) => {
    setShowUrlModal((prev) => ({
      ...prev,
      state,
      sectionIndex,
      eachField,
      tableRowIndex,
      answerValue: answer.value,
      fieldIndex,
      sectionId,
      source,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    }));
  };

  const handleGeoLocationModalState = ({
    sectionIndex,
    eachField,
    tableRowIndex,
    answer,
    fieldIndex,
    sectionId,
    source,
    tableRowId,
    state,
  }: FieldModalStateArgs) => {
    setShowGeoLocationModal({
      sectionIndex,
      eachField,
      tableRowIndex,
      answer,
      fieldIndex,
      sectionId,
      source,
      tableRowId,
      state,
      uniqueId: tableRowId ? `${tableRowId}${eachField!._id}` : eachField!._id,
    });
  };

  const handleSaveUrlData = (handleCustomFieldData: unknown, URLData: { displayText?: string; URL?: string }) => {
    if (!URLData.displayText || !URLData.URL) {
      const field = !URLData.displayText ? "Display Text" : "URL";
      dispatch(
        showToastNotification({
          content: t("{{field}} is Missing", { field }) as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    handleURLModalState({ state: false });
    const answerValue = {
      ...URLData,
    };
    const { eachField, sectionIndex, tableRowIndex, fieldIndex, sectionId, source } = showUrlModal;
    const { _id } = eachField!;

    if (handleCustomFieldData) {
      (handleCustomFieldData as HandleCustomFieldData)({
        eachField: showUrlModal.eachField,
        answerValue,
        fieldIndex: showUrlModal.fieldIndex,
        sectionIndex: showUrlModal.sectionIndex,
        tableRowIndex: showUrlModal.tableRowIndex,
        source: showUrlModal.source,
      });
    } else if (inspectionData._id) {
      if (source == formSectionType.TABLE_SECTION) {
        setInspectionData((prev) => {
          const modifiedTemplate = prev.selectedForm.template;
          const modifiedTableDataSource = modifiedTemplate![sectionIndex!]!.tableDataSource!.map(
            (row: TableRow, index: number) => {
              if (index == tableRowIndex) {
                row[_id] = {
                  createdDate: Date.now(),
                  createdBy: toObjectIdString(userDetails!._id),
                  value: answerValue,
                };
              }
              return row;
            },
          );
          modifiedTemplate![sectionIndex!]!.tableDataSource = modifiedTableDataSource;

          setEachAnswerToSave({
            tableSectionId: sectionId,
            tableSectionDataSource: modifiedTableDataSource,
          });

          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });
      } else {
        setInspectionData((prev) => {
          const modifiedTemplate = prev.selectedForm.template;
          modifiedTemplate![sectionIndex!]!.questionFields![fieldIndex!]!.answer = {
            value: answerValue,
          };
          return {
            ...prev,
            selectedForm: {
              ...prev.selectedForm,
              template: modifiedTemplate,
            },
          };
        });

        setEachAnswerToSave({
          sectionId,
          questionId: _id,
          answer: { value: answerValue },
        });
      }
    }
  };

  function checkWhichSectionsExist({
    section,
    sectionIndex,
    contractDetails,
  }: {
    section: FormInstanceSection;
    sectionIndex: number;
    contractDetails: { userPermissions?: number[] };
  }) {
    const permission: boolean = getUserPermissionForForm(inspectionData, userDetails!.id, sectionIndex);

    let isCurrentUserAllowed = permission;
    const customModuleSectionHeadings = customModules.map((module) => module.name) || [];
    const customModuleSectionCode = customModules.map((module) => module.resourceType) || [];
    if (inspectionData.contractId) {
      isCurrentUserAllowed = permission && contractDetails.userPermissions?.includes(RESOURCE_MODIFY.id)!;
    }

    return {
      isCurrentUserAllowed,
      doesQuantityExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_QUANTITY,
      doesTaskExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TASK,
      doesReferenceExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_REFERENCE,
      doesLinkedFormExist:
        [
          ...customModuleSectionHeadings,
          presetSectionsObject.SECTION_TYPE_INSPECTION,
          presetSectionsObject.SECTION_TYPE_BILLS,
          presetSectionsObject.SECTION_TYPE_CHANGE_ORDERS,
          presetSectionsObject.SECTION_TYPE_PROGRESS_UPDATES,
        ].includes(section.sectionHeading) &&
        [
          ...customModuleSectionCode,
          presetSectionsCodeObject.SECTION_TYPE_BILLS,
          presetSectionsCodeObject.SECTION_TYPE_CHANGE_ORDERS,
          presetSectionsCodeObject.SECTION_TYPE_INSPECTION,
          presetSectionsCodeObject.SECTION_TYPE_PROGRESS_UPDATES,
        ].includes(section.sectionCode),
      doesSectionTypeFormsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_FORM,
      doesMilestoneExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_MILESTONE,
      doesWbsItemsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_WBS_ITEMS,
      doesSummaryOfChangesExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_SUMMARY_OF_CHANGES,
      doesReviewsExist: section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_REVIEWS,
      doesTableSectionExist: section.sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION,
    };
  }

  const getTableSectionShowAnsLogic = (section: FormInstanceSection, field: QuestionField) => {
    const isFinalState = inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status;

    if (isFinalState) return true;

    const hasModifyPermission = getSectionPermission({
      section,
      inspectionData,
      userId: userDetails!.id,
      type: "Modify",
    });

    const isSectionLocked = !hasModifyPermission || !modifySection.includes(section._id!);

    const isPredefinedAllowed =
      !section.allowPreDefineRowValues ||
      (section.allowPreDefineRowValues &&
        (!field.allowPreDefinedColValues || (field.allowPreDefinedColValues && section.allowEditPreDefineRowInForm)));

    return isSectionLocked && !isPredefinedAllowed;
  };

  const getNormalSectionShowAnsLogic = (section: FormInstanceSection, field: QuestionField) => {
    const isFinalState = inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status;

    if (isFinalState) return true;

    const isPredefinedLocked = field.allowPredefinedVal && !field.allowEditingOfPredefinedVal;

    if (isPredefinedLocked) return true;

    const canModifySection =
      getSectionPermission({
        section,
        inspectionData,
        userId: userDetails!.id,
        type: "Modify",
      }) && !modifySection.includes(section._id!);

    return canModifySection;
  };

  return {
    inspectionData,
    isFileUploading,
    setInspectionData,
    showAttachmentModal,
    setShowAttachmentModal,
    attachmentModalOpenedFor,
    setAttachmentModalOpenedFor,
    sideBarTaskDetails,
    setSideBarTaskDetails,
    eachAnswerToSave,
    setEachAnswerToSave,
    answerSaved,
    setAnswerSaved,
    lastSaved,
    setLastSaved,
    openCommentDialog,
    setOpenCommentDialog,
    formActionDetails,
    setFormActionDetails,
    accordionExpandedState,
    setAccordionExpandedState,
    showSideBar,
    setShowSideBar,
    showAddUsersModal,
    setShowAddUsersModal,
    showWarningModal,
    setShowWarningModal,
    modifySection,
    setModifySection,
    sideBarAccordions,
    setInitialData,
    handleInputChange,
    setEachAnswer,
    openAddUserModal,
    closeUsersModal,
    saveInspectionData,
    removeUserFromList,
    verifySection,
    handleAttachmentClick,
    closeAttachmentModal,
    handleModifyCancel,
    verifyToggleStatus,
    handleFormActionClick,
    beforeOpenActionDropdown,
    handleFormStateChange,
    handleCollapseAll,
    handleExpandAll,
    handleAccStateChange,
    showToastAndRedirect,
    handleSubmitClick,
    handleFormSubmission,
    closeCommentDialog,
    handleShowWarningModal,
    hideWarningModal,
    userDetails,
    projectData,
    handleSaveCmntClick,
    teamData,
    setTeamData,
    loactionData,
    setLoactionData,
    setIsDataFetched,
    isDataFetched,
    openAddedUserModalInMobile,
    onClickWarningModalConfirm,
    handleFileUploadChange,
    handleRemoveUploadedFile,
    handleRemoveAllUploadedFiles,
    setTableSectionConfig,
    tableSectionConfig,
    handleExportPdf,
    actionBtnDisabled,
    formObject,
    handleDeleteTableRow,
    handleAddTableRowClick,
    handleAddUserClick,
    getTeamDataForMobile,
    getLocationForMobile,
    showWaitSpinner,
    selectSignType,
    saveEditedImage,
    verifyAllState,
    imageFieldSelectedIndex,
    setImageFieldSelectedIndex,
    customModules,
    formValidator,
    setOpenExportModal,
    openExportModal,
    handleURLModalState,
    showUrlModal,
    setShowUrlModal,
    handleSaveUrlData,
    handleChangeAnswer,
    ActionItems,
    showGeoLocationModal,
    setShowGeoLocationModal,
    handleGeoLocationModalState,
    checkWhichSectionsExist,
    getTableSectionShowAnsLogic,
    getNormalSectionShowAnsLogic,
    commentSaveDisabledState,
    levelEditModal,
    setLevelEditModal,
  };
};
export default useForms;
